import { Theme, useTheme } from "@emotion/react";
import {
  Alert,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
} from "@mui/material";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { pick } from "lodash";
import { useMemo } from "react";
import { Form, FormProps } from "react-final-form";
import { useIntl } from "react-intl";
import {
  AdminContentItem,
  SaveContentRequest,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { CommonFormProps } from "types/common-types";
import { isEmptyOrSpaces } from "utils/formUtils";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

type ContentBlockFormProps = CommonFormProps<
  AdminContentItem,
  SaveContentRequest
> &
  FormProps<SaveContentRequest>;

export function ContentBlockForm({
  contentBlock,
  submitLabel,
  onSubmit,
  onCancel,
}: ContentBlockFormProps) {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { organizationName } = contextStore.useState();

  const init = useMemo(
    () => ({
      ...pick(contentBlock ?? {}, ["id", "name", "description", "value"]),
    }),
    [contentBlock]
  );

  const isEditMode = !!contentBlock?.id;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  variant="standard"
                  fullWidth
                  disabled
                  label={t({ id: "organization" })}
                  value={organizationName}
                />
              </Grid>

              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "nameLabel" })}
                      disabled={isEditMode}
                      error={meta.error && meta.submitFailed}
                      helperText={
                        meta.error && meta.submitFailed && meta.error
                      }
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="description">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "descriptionLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={
                        meta.error && meta.submitFailed && meta.error
                      }
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="value" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FieldRichText
                      input={input}
                      meta={meta}
                      label={t({ id: "valueLabel" })}
                      init={{
                        height: 400,
                        convert_urls: false,
                      }}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>

            <Button
              type="submit"
              color="primary"
              disabled={
                submitting ||
                isEmptyOrSpaces(values?.name) ||
                isEmptyOrSpaces(values?.value)
              }
            >
              {submitLabel}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
}
